import { datadogRum } from "@datadog/browser-rum";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Modal } from "PFComponents/modal";
import { Select } from "PFComponents/select";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import { getProfileName } from "PFCore/helpers/profile";
import { useShareSavedFilters } from "PFCore/hooks/queries/filters/use_share_saved_filters";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { fetchProfilesOptions } from "PFCore/services/profile";
import { Option } from "PFTypes";
import { BasicProfile, Id } from "PFTypes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { DD_SAVED_FILTER_SHARE } from "../../datadog_actions";
import { ProfileItem } from "./parts/profile_item";

type SavedFiltersCollectionShareModalProps = {
  id: Id;
  name: string;
  onClose: VoidFunction;
};

export const SavedFiltersCollectionShareModal = ({
  id,
  name,
  onClose
}: SavedFiltersCollectionShareModalProps) => {
  const { t } = useTranslation("filters", { keyPrefix: "share" });

  const { data: currentProfile } = useCurrentProfile();
  const { showGrowl } = useGrowlStoreActions();

  const [viewName, setViewName] = useState<string | null>(name);
  const [profilesToShare, setProfilesToShare] = useState<BasicProfile[]>([]);

  const { mutate: shareSavedFilters } = useShareSavedFilters({
    onSuccess: () => {
      showGrowl({
        kind: "success",
        message: t("onSuccess")
      });
    },
    onError: () => {
      showGrowl({
        kind: "error",
        message: t("onError")
      });
    }
  });

  const handleShare = () => {
    datadogRum.addAction(DD_SAVED_FILTER_SHARE, {
      id,
      name,
      profilesToShareCount: profilesToShare.length
    });

    shareSavedFilters({
      id,
      data: {
        profilesIds: profilesToShare.map(({ id: profileId }) => profileId),
        name: viewName?.trim() || ""
      }
    });
    setViewName(null);
  };

  const isAcceptEnabled = viewName && viewName.trim() && profilesToShare.length > 0;

  return (
    <Modal
      title={t("filterNameModalTitle")}
      onOK={isAcceptEnabled ? handleShare : undefined}
      onClose={() => {
        setViewName(null);
        onClose();
      }}
      labelOK={t("shareButton")}
    >
      <InputFieldSet
        label={t("filterName")}
        value={viewName ?? ""}
        maxLength={100}
        required
        onChange={setViewName}
      />
      <Select<BasicProfile>
        label={t("shareWith")}
        multiple
        value={profilesToShare.map(({ id: profileId }) => profileId)}
        fetchOptions={({ searchTerm, ids }) => {
          if (ids) {
            return Promise.resolve({
              entries: profilesToShare.map((profile) => ({
                id: profile.id,
                value: getProfileName(profile),
                original: profile
              })),
              meta: { total: profilesToShare.length }
            });
          }
          return fetchProfilesOptions({ term: searchTerm, value: ids }).then((data) => ({
            ...data,
            entries: data.entries
              .filter((item) => ids || item.id !== currentProfile.id)
              .map(
                (item): Option<BasicProfile> => ({
                  id: item.id,
                  value: getProfileName(item),
                  displayElement: ids ? undefined : (
                    <ProfileItem profileName={getProfileName(item)} email={item.email} />
                  ),
                  original: item
                })
              )
          }));
        }}
        onChange={(options: Option<BasicProfile>[]) => {
          setProfilesToShare(options.map((opt) => opt.original));
        }}
        onClear={profilesToShare.length ? () => setProfilesToShare([]) : undefined}
      />
    </Modal>
  );
};
